import {
  FUND_DEFINITION,
  useObservableValue,
  useSubscription,
  wsScanToMap,
  type FundDefinition,
  type SubscriptionResponse,
} from '@talos/kyoko';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { map, shareReplay } from 'rxjs';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export type IFundDefinitionContext = {
  fundsBySymbol: Map<string, FundDefinition>;
  fundsList: FundDefinition[];
};

export const FundDefinitionContext = createContext<IFundDefinitionContext | undefined>(undefined);

export function useFundDefinitions() {
  const context = useContext(FundDefinitionContext);
  if (!context) {
    throw new Error('useFundDefinitionProvider must be used within a FundDefinitionContext');
  }
  return context;
}

export function FundDefinitionProvider({ children }: PropsWithChildren) {
  const { useFundDefinitionAndTradableIndexesStreams } = useFeatureFlag();

  const { data } = useSubscription<SubscriptionResponse<FundDefinition>>(
    // Will remove this feature flag when we switch from baskets to just FundDefinitions
    useFundDefinitionAndTradableIndexesStreams
      ? {
          name: FUND_DEFINITION,
          tag: 'FundDefinitionProvider',
        }
      : null
  );

  const fundsBySymbolObs = useMemo(
    () =>
      data.pipe(
        wsScanToMap({ getUniqueKey: f => f.Symbol, newMapEachUpdate: true }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [data]
  );

  const fundsListObs = useMemo(
    () =>
      fundsBySymbolObs.pipe(
        map(mapping => [...mapping.values()]),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
    [fundsBySymbolObs]
  );

  const fundsBySymbol = useObservableValue(() => fundsBySymbolObs, [fundsBySymbolObs], new Map());
  const fundsList = useObservableValue(() => fundsListObs, [fundsListObs], []);

  const value = useMemo(() => ({ fundsBySymbol, fundsList }), [fundsBySymbol, fundsList]);

  return <FundDefinitionContext.Provider value={value}>{children}</FundDefinitionContext.Provider>;
}
